/**
 * Content Service
 */

import { httpClient } from '../http-client';

const API_BASE_URL = '/api/v1';

export interface GenerateLLMContentResponse {
  contentId: string;
  status: string;
  workflowId?: string;
}

export interface LLMContent {
  id: string;
  topicNodeId: string;
  revision: number;
  content: string;
  style?: string;
  status: string;
  createdAt: string;
}

export interface UserContentSegment {
  segmentKey: string;
  content: string;
  order: number;
  isCustom?: boolean;
  isDeleted?: boolean;
}

export interface UserContentResponse {
  id: string;
  topicNodeId: string;
  segments: UserContentSegment[];
  style?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BatchControlResponse {
  message: string;
  status: string;
}

export interface BatchStatusResponse {
  status: string;
  totalNodes?: number;
  completedNodes?: number;
  failedNodes?: number;
  currentNode?: string;
}

export const ContentService = {
  /**
   * Generate LLM content for a topic node (JSON response)
   */
  async generateLLMContent(
    topicNodeId: string,
    forceNewRevision: boolean = false
  ): Promise<GenerateLLMContentResponse> {
    const url = `${API_BASE_URL}/llm-content/generate`;

    const response = await httpClient.post<GenerateLLMContentResponse>(url, {
      topicNodeId,
      forceNewRevision
    });

    return response.data;
  },

  /**
   * Get latest LLM content for a topic node
   */
  async getLatestLLMContent(topicNodeId: string): Promise<LLMContent> {
    try {
      const url = `${API_BASE_URL}/llm-content/topic/${topicNodeId}/latest`;
      const response = await httpClient.get<LLMContent>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching latest LLM content:', error);
      throw error;
    }
  },

  /**
   * Get user content for a topic node (auto-creates if not exists)
   * @param topicNodeId - The topic node ID
   * @param style - Optional content style (feature, study, storytelling, blog, tutorial)
   */
  async getUserContent(topicNodeId: string, style?: string): Promise<UserContentResponse> {
    try {
      const url = style
        ? `${API_BASE_URL}/user-content/${topicNodeId}?style=${encodeURIComponent(style)}`
        : `${API_BASE_URL}/user-content/${topicNodeId}`;
      const response = await httpClient.get<UserContentResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching user content:', error);
      throw error;
    }
  },

  /**
   */
  async updateUserContentSegments(
    topicNodeId: string,
    segments: Array<{
      segmentKey: string;
      content: string;
      order: number;
      isCustom?: boolean;
      isDeleted?: boolean;
    }>
  ): Promise<UserContentResponse> {
    try {
      const url = `${API_BASE_URL}/user-content/${topicNodeId}`;
      const response = await httpClient.put<UserContentResponse>(url, { segments });
      return response.data;
    } catch (error) {
      console.error('Error updating user content segments:', error);
      throw error;
    }
  },

  /**
   * Control content generation batch (pause/resume/terminate)
   */
  async controlContentBatch(nodeId: string, action: 'pause' | 'resume' | 'terminate'): Promise<BatchControlResponse> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${nodeId}/batch/control`;
      const response = await httpClient.post<BatchControlResponse>(url, { action });
      return response.data;
    } catch (error) {
      console.error('Error controlling content batch:', error);
      throw error;
    }
  },

  /**
   * Get content batch status for a node
   */
  async getNodeBatchStatus(nodeId: string): Promise<BatchStatusResponse> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${nodeId}/batch/status`;
      const response = await httpClient.get<BatchStatusResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Error getting node batch status:', error);
      throw error;
    }
  },

  /**
   * Get active batch status for a learning tree
   */
  async getTreeBatchStatus(treeId: string): Promise<BatchStatusResponse> {
    try {
      const url = `${API_BASE_URL}/learning-trees/${treeId}/batch/status`;
      const response = await httpClient.get<BatchStatusResponse>(url);
      return response.data;
    } catch (error) {
      console.error('Error getting tree batch status:', error);
      throw error;
    }
  },

  /**
   * Get style status for a topic node
   */
  async getStyleStatus(nodeId: string): Promise<{
    nodeId: string;
    styles: Record<string, { status: string; llmContentId: string | null }>;
  }> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${nodeId}/style-status`;
      const response = await httpClient.get<{
        nodeId: string;
        styles: Record<string, { status: string; llmContentId: string | null }>;
      }>(url);
      return response.data;
    } catch (error) {
      console.error('Error getting style status:', error);
      throw error;
    }
  },

  /**
   * Generate content for a specific style
   */
  async generateStyleContent(
    nodeId: string,
    style: 'insight' | 'study' | 'narrative' | 'blog' | 'tutorial'
  ): Promise<{
    message: string;
    nodeId: string;
    style: string;
    workflowId: string;
  }> {
    try {
      const url = `${API_BASE_URL}/topic-nodes/${nodeId}/generate-style`;
      const response = await httpClient.post<{
        message: string;
        nodeId: string;
        style: string;
        workflowId: string;
      }>(url, { style });
      return response.data;
    } catch (error) {
      console.error('Error generating style content:', error);
      throw error;
    }
  },
};
